import React, { useCallback, useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { clearAuthSessionClientState } from "../Utils/axiosInterceptors";
import { toast } from "react-toastify";
import { Button, Modal, ModalBody, ModalFooter, ModalHeader } from "reactstrap";
import propic from "../Utils/defaultUserAvatar";
import axiosInterceptor from "../Utils/axiosInterceptors";
import { apiEndpoints } from "../Utils/ApiConfig";
import { playNotificationChime } from "../Utils/playNotificationSound";
import { clearSchoolUserInfo } from "../Store/Reducer-Actions/AuthStatusSlice";
import { clearSchoolInfo } from "../Store/Reducer-Actions/SchoolInfoSlice";
import PageLoader from "../Components/Loader/PageLoader";

const NOTIFICATION_ROLES = new Set([1, 2, 3, 4, 7, 13, 14]);

const Header = () => {
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const username = useSelector((state) => state.authInfo?.data?.username);
    const role = useSelector((state) => state.authInfo?.data?.role);
    const profileImageUrl = useSelector((state) => state.authInfo?.data?.profile_image_url);
    const schoolName = useSelector((state) => state.authInfo?.data?.school_name);
    const schoolBranch = useSelector((state) => state.authInfo?.data?.school_branch);
    const schoolAddress = useSelector((state) => state.authInfo?.data?.school_address);
    const schoolHeaderTitle = [schoolName, schoolBranch].filter(Boolean).join(" - ");
    const avatarSrc = profileImageUrl || propic;

    const [logoutConfirmOpen, setLogoutConfirmOpen] = useState(false);
    const [logoutLoading, setLogoutLoading] = useState(false);

    const roleLabel = {
        1: "School Head",
        2: "School Admin",
        3: "Faculty",
        4: "Class Teacher",
        5: "Parent",
        6: "Student",
        7: "Thrillion Customer Support",
        8: "Super Admin",
        9: "Clerk",
        11: "Luxehara Admin",
        13: "Support Lead",
        14: "Support Manager",
        15: "Vendor Admin",
        16: "Vendor Staff",
    }[Number(role)] || "User";

    const profileRef = useRef(null);
    const notifyRef = useRef(null);

    const notificationsEnabled = NOTIFICATION_ROLES.has(Number(role));
    const [notifyUnread, setNotifyUnread] = useState(0);
    const [notifyItems, setNotifyItems] = useState([]);
    const [notifyLoading, setNotifyLoading] = useState(false);
    const prevUnreadRef = useRef(null);

    const closeAll = () => {
        profileRef.current?.classList.remove("show");
        notifyRef.current?.classList.remove("show");
    };

    const toggle = (ref) => {
        const isOpen = ref.current.classList.contains("show");
        closeAll();
        if (!isOpen) ref.current.classList.add("show");
    };

    const refreshUnread = useCallback(async () => {
        if (!notificationsEnabled) return;
        try {
            const res = await axiosInterceptor.get(
                apiEndpoints.faculty_notifications_unread_count,
                { withCredentials: true }
            );
            const n = Number(res?.data?.unread_count ?? 0);
            const prev = prevUnreadRef.current;
            if (prev !== null && n > prev) {
                playNotificationChime();
            }
            prevUnreadRef.current = n;
            setNotifyUnread(n);
        } catch {
            /* ignore */
        }
    }, [notificationsEnabled]);

    const dedupeUnreadNotifications = (items) => {
        const seen = new Set();
        return (items || []).filter((n) => {
            if (n?.is_read) return false;
            if (n?.kind !== "ticket_message") return true;
            const key = `${n.ticket_id || ""}|${(n.title || "").trim()}|${(n.body || "").trim()}`;
            if (seen.has(key)) return false;
            seen.add(key);
            return true;
        });
    };

    const loadNotifications = useCallback(async () => {
        if (!notificationsEnabled) return;
        setNotifyLoading(true);
        try {
            const res = await axiosInterceptor.get(apiEndpoints.faculty_notifications, {
                params: { unread: true },
                withCredentials: true,
            });
            const raw = Array.isArray(res.data) ? res.data : [];
            setNotifyItems(dedupeUnreadNotifications(raw));
        } catch {
            setNotifyItems([]);
        } finally {
            setNotifyLoading(false);
        }
    }, [notificationsEnabled]);

    useEffect(() => {
        if (!notificationsEnabled) return undefined;
        refreshUnread();
        const id = setInterval(refreshUnread, 25000);
        return () => clearInterval(id);
    }, [notificationsEnabled, refreshUnread]);

    useEffect(() => {
        const onDoc = () => closeAll();
        document.addEventListener("click", onDoc);
        return () => document.removeEventListener("click", onDoc);
    }, []);

    useEffect(() => {
        const observer = new MutationObserver(() => {
            if (document.body.classList.contains("modal-open")) {
                closeAll();
            }
        });
        observer.observe(document.body, { attributes: true, attributeFilter: ["class"] });
        return () => observer.disconnect();
    }, []);

    const toggleNotifyDropdown = (e) => {
        e.stopPropagation();
        const ref = notifyRef;
        const isOpen = ref.current?.classList.contains("show");
        closeAll();
        if (!isOpen) {
            ref.current?.classList.add("show");
            loadNotifications();
            refreshUnread();
        }
    };

    const resolveNotificationPath = (notif) => {
        if (notif?.action_path) return notif.action_path;
        const ticketId = notif?.ticket_id ?? notif?.ticket;
        if (ticketId) return `/ticket-info?ticket_id=${ticketId}`;
        if (notif?.kind === "assigned_task") return "/faculty/view-tasks";
        if (notif?.kind === "billing_reminder") return "/dashboard";
        if (notif?.kind === "faculty_leave_request") return "/school-leave-requests-info";
        if (notif?.kind === "faculty_leave_decision") return "/faculty-leave-details";
        if (notif?.kind === "ticket_message") {
            const r = Number(role);
            if ([7, 13, 14, 8].includes(r)) return "/support-lead/tickets";
            if ([1, 2].includes(r)) return "/school-admin-tickets?tab=view";
        }
        return null;
    };

    const markNotificationRead = useCallback(
        async (notif) => {
            if (!notif?.id || notif.is_read) return;
            setNotifyItems((prev) => prev.filter((x) => x.id !== notif.id));
            setNotifyUnread((prev) => Math.max(0, prev - 1));
            try {
                await axiosInterceptor.post(
                    `${apiEndpoints.faculty_notifications}${notif.id}/read/`,
                    {},
                    { withCredentials: true }
                );
                await refreshUnread();
            } catch {
                loadNotifications();
                refreshUnread();
            }
        },
        [refreshUnread, loadNotifications]
    );

    const handleNotificationOpen = (e, notif) => {
        e.stopPropagation();
        e.preventDefault();
        closeAll();
        markNotificationRead(notif);
        const path = resolveNotificationPath(notif);
        if (path) navigate(path);
    };

    const openProfile = (event) => {
        event.stopPropagation();
        closeAll();
        navigate("/profile");
    };

    const openSettings = (event) => {
        event.stopPropagation();
        closeAll();
        navigate("/settings");
    };

    const requestLogout = (e) => {
        e?.stopPropagation?.();
        closeAll();
        setLogoutConfirmOpen(true);
    };

    const closeLogoutModal = () => {
        if (logoutLoading) return;
        setLogoutConfirmOpen(false);
    };

    const performLogout = () => {
        setLogoutLoading(true);
        axios
            .post(apiEndpoints.logoutApi, {}, { withCredentials: true })
            .then(() => {
                clearAuthSessionClientState();
                dispatch(clearSchoolUserInfo());
                dispatch(clearSchoolInfo());
                toast.success("Logged out successfully.", {
                    position: toast.POSITION?.TOP_RIGHT,
                    autoClose: 1500,
                });
                setLogoutConfirmOpen(false);
                setTimeout(() => {
                    navigate("/login");
                    window.location.reload();
                }, 400);
            })
            .catch(() => {
                toast.error("Logout failed. Please try again.", {
                    position: toast.POSITION?.TOP_RIGHT,
                    autoClose: 2000,
                });
            })
            .finally(() => {
                setLogoutLoading(false);
            });
    };

    return (
        <div id="header-card" onClick={(e) => e.stopPropagation()}>
            <div className="header-wrapper">
                {schoolHeaderTitle ? (
                    <div
                        className="header-school-title"
                        title={[schoolHeaderTitle, schoolAddress].filter(Boolean).join("\n")}
                    >
                        <span className="header-school-title__name">{schoolHeaderTitle}</span>
                        {schoolAddress ? (
                            <span className="header-school-title__address">{schoolAddress}</span>
                        ) : null}
                    </div>
                ) : null}
                <div
                    className="icon-wrapper header-notify-wrap"
                    onClick={toggleNotifyDropdown}
                    role="button"
                    tabIndex={0}
                    onKeyDown={(e) => {
                        if (e.key === "Enter" || e.key === " ") {
                            e.preventDefault();
                            toggleNotifyDropdown(e);
                        }
                    }}
                >
                    <i className="icon-base bx bx-bell icon-md" aria-hidden="true" />
                    {notificationsEnabled && notifyUnread > 0 ? (
                        <span className="header-notify-count" aria-label={`${notifyUnread} unread notifications`}>
                            {notifyUnread > 99 ? "99+" : notifyUnread}
                        </span>
                    ) : null}
                    <div
                        className="dropdown-menu header-notify-dropdown"
                        ref={notifyRef}
                        onClick={(e) => e.stopPropagation()}
                    >
                        <div className="header-notify-card">
                            <div className="header-notify-card__header">
                                <div className="header-notify-card__title-row">
                                    <span className="header-notify-card__title">Notifications</span>
                                    {notificationsEnabled && notifyUnread > 0 ? (
                                        <span className="header-notify-card__pill">{notifyUnread} new</span>
                                    ) : null}
                                </div>
                                {notificationsEnabled ? (
                                    <p className="header-notify-card__subtitle">
                                        Unread only — click an item to open and mark it read
                                    </p>
                                ) : null}
                            </div>
                            <div className="header-notify-card__body">
                                {!notificationsEnabled ? (
                                    <p className="header-notify-card__empty">Nothing to show</p>
                                ) : notifyLoading ? (
                                    <PageLoader inline size="sm" label="Loading notifications" />
                                ) : notifyItems.length === 0 ? (
                                    <p className="header-notify-card__empty">No unread notifications.</p>
                                ) : (
                                    <ul className="header-notify-list" role="list">
                                        {notifyItems.map((n) => (
                                            <li key={n.id} className="header-notify-list__item">
                                                <button
                                                    type="button"
                                                    className="header-notify-item"
                                                    onClick={(e) => handleNotificationOpen(e, n)}
                                                >
                                                    <div className="header-notify-item__top">
                                                        <span className="header-notify-item-title">{n.title}</span>
                                                        <span className="header-notify-item-pill" aria-hidden>
                                                            New
                                                        </span>
                                                    </div>
                                                    {n.body ? (
                                                        <span className="header-notify-item-body">{n.body}</span>
                                                    ) : null}
                                                </button>
                                            </li>
                                        ))}
                                    </ul>
                                )}
                            </div>
                        </div>
                    </div>
                </div>

                <div
                    className="pro-icon-wrapper profile-wrapper"
                    onClick={(e) => {
                        e.stopPropagation();
                        toggle(profileRef);
                    }}
                >
                    <img src={avatarSrc} alt="profile" className="profile-img" />
                    <div className="profile-text">
                        <p className="username">{username}</p>
                        <p className="role">{roleLabel}</p>
                    </div>

                    <div
                        id="header-profile-dropdown"
                        className="dropdown-menu profile-menu"
                        ref={profileRef}
                    >
                        <div className="profile-dropdown-header">
                            <img src={avatarSrc} alt="profile" className="dropdown-avatar" />
                            <div>
                                <p className="dropdown-username">{username}</p>
                                <p className="dropdown-role">{roleLabel}</p>
                            </div>
                        </div>

                        <div className="dropdown-divider" />

                        <div className="dropdown-item ux-item" onClick={openProfile}>
                            <i className="bx bx-user dropdown-icon" aria-hidden="true" />
                            <span className="dropdown-item-name">My Profile</span>
                        </div>

                        <div className="dropdown-item ux-item" onClick={openSettings}>
                            <i className="bx bx-cog dropdown-icon" aria-hidden="true" />
                            <span className="dropdown-item-name">Settings</span>
                        </div>

                        <div className="dropdown-divider" />

                        <div
                            className="dropdown-item ux-item logout"
                            role="button"
                            tabIndex={0}
                            onClick={requestLogout}
                            onKeyDown={(e) => {
                                if (e.key === "Enter" || e.key === " ") {
                                    e.preventDefault();
                                    requestLogout(e);
                                }
                            }}
                        >
                            <i className="bx bx-log-out dropdown-icon" aria-hidden="true" />
                            <span className="dropdown-item-name">Logout</span>
                        </div>
                    </div>
                </div>
            </div>

            <Modal isOpen={logoutConfirmOpen} toggle={closeLogoutModal} centered>
                <ModalHeader toggle={closeLogoutModal}>Confirm logout</ModalHeader>
                <ModalBody>Are you sure you want to log out?</ModalBody>
                <ModalFooter>
                    <Button color="secondary" outline onClick={closeLogoutModal} disabled={logoutLoading}>
                        Cancel
                    </Button>
                    <Button color="danger" onClick={performLogout} disabled={logoutLoading}>
                        {logoutLoading ? "Logging out…" : "Logout"}
                    </Button>
                </ModalFooter>
            </Modal>
        </div>
    );
};

export default Header;
